import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu';
import { useTheme, type Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';

const OPTIONS: Array<{ v: Theme; label: string; icon: typeof SunIcon }> = [
  { v: 'light', label: 'Light', icon: SunIcon },
  { v: 'dark', label: 'Dark', icon: MoonIcon },
  { v: 'system', label: 'System', icon: MonitorIcon },
];

/** Radio items to embed in a dropdown menu. */
export function ThemeMenuItems() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
      {OPTIONS.map(({ v, label, icon: Icon }) => (
        <DropdownMenuRadioItem key={v} value={v}>
          <Icon /> {label}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

/**
 * Segmented control for settings pages. `compact` renders icon-only buttons; `onDark` restyles the
 * control for a dark brand surface (the sign-in canvas) instead of the page theme.
 */
export function ThemeSegmented({ className, compact = false, onDark = false }: { className?: string; compact?: boolean; onDark?: boolean }) {
  const { theme, setTheme } = useTheme();
  return (
    <div role="radiogroup" aria-label="Color theme" className={cn('inline-flex border', onDark && 'border-brand-panel-foreground/25', className)}>
      {OPTIONS.map(({ v, label, icon: Icon }) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={theme === v}
          aria-label={compact ? label : undefined}
          title={compact ? label : undefined}
          onClick={() => setTheme(v)}
          className={cn(
            'flex items-center gap-2 border-r text-sm transition-colors outline-none last:border-r-0 focus-visible:ring-[3px] focus-visible:ring-ring/50',
            compact ? 'size-9 justify-center' : 'px-3 py-1.5',
            onDark && 'border-brand-panel-foreground/25',
            theme === v
              ? onDark
                ? 'bg-brand-panel-foreground text-brand-panel'
                : 'bg-primary text-primary-foreground'
              : onDark
                ? 'text-brand-panel-foreground/70 hover:bg-brand-panel-foreground/10 hover:text-brand-panel-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )}
        >
          <Icon className="size-4" />
          {!compact && label}
        </button>
      ))}
    </div>
  );
}

/** Single icon button that flips between light and dark (used on the sign-in shell). */
export function ThemeToggleButton({ className }: { className?: string }) {
  const { resolved, setTheme } = useTheme();
  const next = resolved === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      className={cn(
        'text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:ring-ring/50 inline-flex size-9 items-center justify-center border transition-colors outline-none focus-visible:ring-[3px]',
        className,
      )}
    >
      {resolved === 'dark' ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
    </button>
  );
}
